import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type ConsentValue = "granted" | "denied";

interface CookieConsentState {
  /** null = nog geen keuze gemaakt */
  marketing: ConsentValue | null;
  accept: () => void;
  decline: () => void;
  /** Banner opnieuw tonen zodat de bezoeker de keuze kan wijzigen */
  reopen: () => void;
  bannerVisible: boolean;
}

const STORAGE_KEY = "nc-cookie-consent";

const CookieConsentContext = createContext<CookieConsentState | undefined>(undefined);

function readStored(): ConsentValue | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

export const CookieConsentProvider = ({ children }: { children: React.ReactNode }) => {
  const [marketing, setMarketing] = useState<ConsentValue | null>(null);
  const [bannerVisible, setBannerVisible] = useState(false);

  useEffect(() => {
    const stored = readStored();
    setMarketing(stored);
    setBannerVisible(stored === null);
  }, []);

  const store = useCallback((value: ConsentValue) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* opslag geblokkeerd, keuze geldt dan voor deze sessie */
    }
    setMarketing(value);
    setBannerVisible(false);
  }, []);

  const value = useMemo<CookieConsentState>(
    () => ({
      marketing,
      bannerVisible,
      accept: () => store("granted"),
      decline: () => store("denied"),
      reopen: () => setBannerVisible(true),
    }),
    [marketing, bannerVisible, store],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent moet binnen CookieConsentProvider gebruikt worden");
  return ctx;
}
